import { useCallback, useMemo } from "react";

import { ExecutorFeedback } from "../../services";
import { sortByNumberOrString } from "../../utils";

const useExecutorFeedbackSubHandler =
	(): useExecutorFeedbackSubHandler.Props => {
		enum ColumnId {
			CreatedAt = "createdAt",
			Rating = "rating",
			Comment = "comment",
			Customer = "customer",
			OrderNumber = "orderNumber",
		}

		const formatCustomer = useCallback(
			(client?: ExecutorFeedback.Model["client"]): string => {
				if (!client?.person) return "";

				const { lastName, firstName, fatherName, phones } =
					client.person;

				const fullName = [lastName, firstName, fatherName]
					.filter(Boolean)
					.join(" ");
				const phone = phones?.length ? ` (${phones[0]})` : "";

				return fullName + phone;
			},
			[],
		);

		const getSortAndLimitExecutorFeedbackSubHandler = useCallback(
			(
				{
					limit,
					order,
				}: useExecutorFeedbackSubHandler.SmallSubscribeOptions,
				roleSubscribe: ExecutorFeedback.Model[],
			): ExecutorFeedback.Model[] => {
				if (!Array.isArray(roleSubscribe)) {
					return [];
				}

				let retval = [...(roleSubscribe || [])];

				const [isOrder, isLimit] = [
					typeof order === "object" &&
						order !== null &&
						!Array.isArray(order),
					typeof limit === "number",
				];

				if (isOrder) {
					const [[dataKey, sortType] = []] = Object.entries(
						order || {},
					);
					const isSort =
						typeof sortType === "string" &&
						typeof dataKey === "string" &&
						["asc", "desc"].includes(sortType.toLowerCase());

					if (isSort) {
						const isAscending = sortType.toLowerCase() === "asc";
						const sortOrder = isAscending ? 1 : -1;

						if (dataKey === ColumnId.CreatedAt) {
							const sort = retval.sort((prev, next) => {
								const prevValue = prev.createdAt
									? Date.parse(prev.createdAt) || 0
									: 0;
								const nextValue = next.createdAt
									? Date.parse(next.createdAt) || 0
									: 0;
								return (
									sortByNumberOrString(prevValue, nextValue) *
									sortOrder
								);
							});

							retval = sort;
						}

						if (dataKey === ColumnId.Rating) {
							const sort = retval.sort((prev, next) => {
								const prevValue = prev.rating ?? -1;
								const nextValue = next.rating ?? -1;
								return (
									sortByNumberOrString(prevValue, nextValue) *
									sortOrder
								);
							});

							retval = sort;
						}

						if (dataKey === ColumnId.OrderNumber) {
							const sort = retval.sort((prev, next) => {
								const prevValue = prev.order?.orderNumber ?? -1;
								const nextValue = next.order?.orderNumber ?? -1;
								return (
									sortByNumberOrString(prevValue, nextValue) *
									sortOrder
								);
							});

							retval = sort;
						}

						if (dataKey === ColumnId.Comment) {
							const sort = retval.sort((prev, next) => {
								const prevValue = `${prev?.comment}` || "";
								const nextValue = `${next?.comment}` || "";

								return (
									sortByNumberOrString(prevValue, nextValue) *
									sortOrder
								);
							});

							retval = sort;
						}

						if (dataKey === ColumnId.Customer) {
							const sort = retval.sort((prev, next) => {
								const prevValue = formatCustomer(prev.client);
								const nextValue = formatCustomer(next.client);

								return (
									sortByNumberOrString(prevValue, nextValue) *
									sortOrder
								);
							});

							retval = sort;
						}
					}
				}

				if (isLimit) {
					retval = retval.slice(0, limit);
				}

				return retval;
			},
			[
				ColumnId.Comment,
				ColumnId.CreatedAt,
				ColumnId.Customer,
				ColumnId.OrderNumber,
				ColumnId.Rating,
				formatCustomer,
			],
		);

		return useMemo(
			() => ({
				getSortAndLimitExecutorFeedbackSubHandler,
			}),
			[getSortAndLimitExecutorFeedbackSubHandler],
		);
	};

declare namespace useExecutorFeedbackSubHandler {
	interface Props {
		getSortAndLimitExecutorFeedbackSubHandler: (
			data: SmallSubscribeOptions,
			roleSubscribe: ExecutorFeedback.Model[],
		) => ExecutorFeedback.Model[];
	}

	interface SmallSubscribeOptions {
		order?: ExecutorFeedback.Order;
		limit?: number;
	}
}

export default useExecutorFeedbackSubHandler;
